import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { PublicLayout, PageHero } from "../../components/site/Layout";
import { SERVICES, PROCESS, FAQS } from "../../content";
import { publicApi, errMsg } from "../../lib/api";

export function HowItWorks() {
  return (
    <PublicLayout>
      <PageHero eyebrow="How it works" title="A guided path from idea to launch." text="Designed for business owners, not developers. You stay in control of every decision and every price." />
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-20">
        <ol className="space-y-10">
          {PROCESS.map(([t, d], i) => (
            <li key={t} className="grid md:grid-cols-12 gap-4 border-b border-[#0b3d2e]/10 pb-10" data-testid={`process-step-${i}`}>
              <span className="md:col-span-2 font-cinzel text-3xl text-[#b8892b]">{String(i + 1).padStart(2, "0")}</span>
              <div className="md:col-span-10"><h2 className="text-2xl md:text-3xl text-[#062a20]">{t}</h2><p className="mt-2 text-slate-600">{d}</p></div>
            </li>))}
        </ol>
        <div className="mt-12 card-lux bg-[#f4efe2]">
          <h3 className="text-2xl text-[#062a20]">Important to know</h3>
          <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-slate-700">
            <li>The AI design is a <strong>draft concept</strong>, not your finished website.</li>
            <li>Payments are only marked successful after our server confirms them with the payment provider.</li>
            <li>Legal pages and tax rules are flagged for professional verification — we don't claim legal compliance.</li>
          </ul>
        </div>
        <Link to="/build" className="btn-emerald mt-10" data-testid="how-build-cta">Start now <ArrowRight className="w-4 h-4" /></Link>
      </section>
    </PublicLayout>
  );
}

export function Services() {
  return (
    <PublicLayout>
      <PageHero eyebrow="Our services" title="Business solutions, end to end." text="Website development is available to configure online. For our other services, send an enquiry and we'll respond personally." />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s) => (
          <div key={s.key} className={`card-lux flex flex-col ${s.live ? "border-[#b8892b]/60 lg:col-span-2" : ""}`} data-testid={`service-card-${s.key}`}>
            {s.live && <span className="eyebrow">Configure online</span>}
            <h2 className="text-2xl text-[#062a20] mt-1">{s.title}</h2><p className="mt-2 text-sm text-slate-600 flex-1">{s.text}</p>
            <Link to={s.cta} className={`${s.live ? "btn-emerald" : "btn-gold"} mt-6 self-start`} data-testid={`service-cta-${s.key}`}>{s.live ? s.ctaLabel || "Build Your Website" : "Send an enquiry"}</Link>
          </div>))}
      </section>
    </PublicLayout>
  );
}

export function WebsiteDevelopment() {
  const blocks = [
    ["Recommended for your business", "Features are classified as Required, Recommended or Optional based on what you actually need — with a plain reason for each."],
    ["Three packages", "Essential, Recommended and Best Possible — compare them side by side and switch any time."],
    ["Transparent pricing", "One-time and recurring charges, configured taxes, discounts, the advance and remaining balance — all shown before you accept."],
    ["Multi-currency", "View prices in INR, USD, GBP, EUR or AED. We tell you exactly which currency you'll be charged in."],
    ["AI Design Studio", "Preview a draft concept on desktop, tablet and mobile. Change colours, the full theme or the layout — and always restore the original."],
    ["Real delivery", "Our team builds, tests, publishes and hands over your website with documentation."],
  ];
  return (
    <PublicLayout>
      <PageHero eyebrow="Website development" title="AI-assisted planning. Human craftsmanship." text="Configure your website online with full price transparency, then let Altiora build it properly." />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {blocks.map(([t, d]) => (<div key={t} className="card-lux"><h2 className="text-2xl text-[#062a20]">{t}</h2><p className="mt-2 text-sm text-slate-600">{d}</p></div>))}
      </section>
      <div className="max-w-7xl mx-auto px-5 lg:px-8"><Link to="/build" className="btn-emerald" data-testid="webdev-build-cta">Build Your Website <ArrowRight className="w-4 h-4" /></Link></div>
    </PublicLayout>
  );
}

export function About() {
  return (
    <PublicLayout>
      <PageHero eyebrow="About us" title="Ideas. Insights. Solutions." text="Altiora Ventures helps businesses move forward with research, analysis, documentation, presentations, websites, mobile applications and AI-powered solutions." />
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-20 space-y-6 text-slate-700 leading-relaxed">
        <h2 className="text-3xl text-[#062a20]">Our approach</h2>
        <p>We combine practical AI tools with careful human work. AI helps us understand your needs faster and show you options clearly; our team makes sure what we deliver is accurate, tested and right for your business.</p>
        <p>We believe in honest pricing, plain language and never claiming something works until it has been tested.</p>
        <div className="card-lux bg-[#f4efe2] text-sm" data-testid="about-admin-placeholder">
          <p className="eyebrow">Company details</p>
          <p className="mt-2">[Company history, team and registration details — to be added by Altiora administrators.]</p>
        </div>
      </section>
    </PublicLayout>
  );
}

export function Faqs() {
  return (
    <PublicLayout>
      <PageHero eyebrow="FAQs" title="Frequently asked questions." />
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16">
        <Accordion type="single" collapsible data-testid="faq-list">
          {FAQS.map(([q, a], i) => (<AccordionItem key={q} value={`f${i}`}><AccordionTrigger className="text-left text-base" data-testid={`faq-item-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>))}
        </Accordion>
      </section>
    </PublicLayout>
  );
}

export function Contact() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", phone: "", service: params.get("service") || "Website Development", message: "", website: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 2) er.name = "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) er.email = "Please enter a valid email address, like name@company.com.";
    if (f.message.trim().length < 10) er.message = "Please write at least 10 characters so we can help.";
    setErrors(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    try { await publicApi.post("/public/enquiries", { ...f, phone: f.phone || null }); setSent(true); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <div className="space-y-1.5"><Label htmlFor={k}>{label}</Label>
      {k === "message" ? <Textarea id={k} rows={5} value={f[k]} onChange={set(k)} data-testid={`contact-${k}`} aria-invalid={!!errors[k]} /> :
        <Input id={k} value={f[k]} onChange={set(k)} data-testid={`contact-${k}`} aria-invalid={!!errors[k]} {...props} />}
      {errors[k] && <p className="text-xs text-red-700" data-testid={`contact-${k}-error`}>{errors[k]}</p>}</div>);
  return (
    <PublicLayout>
      <PageHero eyebrow="Contact us" title="Let's talk about what you need." />
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 space-y-4 text-sm text-slate-700">
          <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#b8892b]" /><a href="mailto:hello@altiora.ventures" data-testid="contact-email-link">hello@altiora.ventures</a></p>
          <p className="text-slate-500">[Phone and address — to be configured by Altiora]</p>
        </div>
        <div className="lg:col-span-8 card-lux">
          {sent ? <div data-testid="contact-success"><h2 className="text-3xl text-[#062a20]">Thank you.</h2><p className="mt-2 text-slate-600">We've received your enquiry and will reply by email.</p></div> : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" noValidate data-testid="contact-form">
              {field("name", "Your name")}{field("email", "Email", { type: "email" })}{field("phone", "Phone (optional)")}
              <div className="space-y-1.5"><Label htmlFor="service">Service</Label>
                <select id="service" value={f.service} onChange={set("service")} className="w-full h-10 rounded-md border px-3 text-sm bg-white" data-testid="contact-service">
                  {["Website Development", ...SERVICES.slice(1).map((s) => s.title), "Other"].map((s) => <option key={s}>{s}</option>)}</select></div>
              <div className="sm:col-span-2">{field("message", "How can we help?")}</div>
              <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
              <button disabled={busy} className="btn-emerald sm:col-span-2 justify-self-start" data-testid="contact-submit">{busy ? "Sending…" : "Send enquiry"}</button>
            </form>)}
        </div>
      </section>
    </PublicLayout>
  );
}

const LEGAL = {
  privacy: ["Privacy Policy", ["We collect the information you provide (such as name, email, phone, business details) to deliver our services, prepare quotations and manage your project.", "Payment details are processed by our payment provider; we do not store card data.", "We use AI services to analyse the business description you submit. Do not include sensitive personal data in descriptions.", "You may request access to or deletion of your data by emailing hello@altiora.ventures.", "[Data controller details, retention periods and jurisdiction-specific rights — to be completed by Altiora and reviewed by a qualified legal advisor.]"]],
  terms: ["Terms and Conditions", ["Quotations show the scope and price you accept. Accepted quotations are not changed without a revised quotation you accept.", "The advance payment is a configured percentage of the project value. The remaining balance is due before publication unless otherwise agreed in writing.", "AI-generated designs are draft concepts. The final website is developed and tested by Altiora.", "Third-party costs (domains, hosting, provider fees) are paid directly to those providers unless included in your quotation.", "[Governing law, liability limits and full commercial terms — to be completed by Altiora and reviewed by a qualified legal advisor.]"]],
  refunds: ["Refund and Cancellation Policy", ["Refund eligibility depends on the work completed at the time of cancellation.", "Refunds are processed to the original payment method. Settled amounts may differ due to provider fees and currency conversion.", "[Specific refund terms — to be configured by Altiora.]"]],
  cookies: ["Cookie Policy", ["We use essential cookies for sign-in and security.", "[Analytics or marketing cookies, if enabled, will be listed here with consent options.]"]],
  disclaimer: ["AI and Content Disclaimer", ["AI suggestions are guidance only and are reviewed by our team.", "We do not provide legal, tax or financial advice. Items needing professional verification are flagged.", "You are responsible for the accuracy of business claims and content you supply or approve."]],
};

export function Legal() {
  const { page } = useParams();
  const [title, paras] = LEGAL[page] || LEGAL.privacy;
  return (
    <PublicLayout>
      <PageHero eyebrow="Policies" title={title} />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 py-16 space-y-4 text-slate-700" data-testid="legal-content">
        <p className="text-xs text-[#9a7322]">Draft policy — requires review by Altiora's legal advisor before reliance.</p>
        {paras.map((p) => <p key={p}>{p}</p>)}
      </section>
    </PublicLayout>
  );
}
